
import { useState, useCallback } from "react";
import { filterDataByTeam, getCSRFToken, fetchWithAuth } from "../../auth";

export const useFetchUtilityStrike = () => {
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);

	const fetchData = useCallback(async (endpoint, callback) => {
		try {
			setLoaded(false);
			setError('');
			setSuccess(false);
			const response = await fetchWithAuth(
				`${process.env.REACT_APP_ICC_TOOL_API_HOST}${endpoint}`,
				{
					headers: { 'X-CSRFToken': getCSRFToken() },
					credentials: 'include',
				}
			);
			const data = await response.json();
			if (!data) throw new Error('Failed to fetch response');
			callback(data);
			setSuccess(true);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoaded(true);
		}
	}, []);

	return { fetchData, loaded, error, success, setError, setSuccess, setLoaded };
};

export const useUtilityStrikeTeams = (fetchData) => {
	const [teams, setTeams] = useState([]);
	const [team, setTeam] = useState({});

	const getTeams = useCallback(async () => {
		await fetchData('user/teams/', (data) => {
			const userInfo = filterDataByTeam();
			const filteredTeams = userInfo?.filterData
				? data.filter((team) => team.members.includes(userInfo.name))
				: data;
			setTeams(filteredTeams);
			if (filteredTeams.length > 0) setTeam(filteredTeams[0]);
		});
	}, [fetchData]);

	const getTeamDetails = useCallback(async (id) => {
		await fetchData(`teams/${id}/`, setTeam);
	}, [fetchData]);

	return { teams, team, setTeam, getTeams, getTeamDetails };
};


export const useUtilityStrikeForms = (fetchData) => {
	const [utilityStrikeForms, setUtilityStrikeForms] = useState({});

	const getUtilityStrikeForms = useCallback(async () => {
	       await fetchData(`utility-strike/`, (data) => {
		       const forms = data.reduce((acc, form) => {
			       acc[form.id] = {
				       // Only use snake_case fields from backend
				       ...form,
				       formId: form.id, // for UI navigation
				       reporter_name: form.reporter_name,
				       status: form.submission_time == null ? "Draft" : "Submitted"
			       };
			       return acc;
		       }, {});
		       setUtilityStrikeForms(forms);
	       });
	}, [fetchData]);

	// After saving a form, refresh the list
	const saveUtilityStrikeForm = useCallback(async (formData, isNew, id) => {
		const url = isNew
			? `${process.env.REACT_APP_ICC_TOOL_API_HOST}utility-strike/`
			: `${process.env.REACT_APP_ICC_TOOL_API_HOST}utility-strike/${id}/`;
		const method = isNew ? 'POST' : 'PUT';
		const response = await fetchWithAuth(url, {
			method: method,
			headers: {
				'Content-Type': 'application/json',
				'X-CSRFToken': getCSRFToken()
			},
			credentials: 'include',
			body: JSON.stringify(formData)
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.detail || 'Failed to save Utility Strike form');
		}
		await getUtilityStrikeForms(); // Refresh list after save
		return await response.json();
	}, [getUtilityStrikeForms]);

	const getUtilityStrikeFormDetails = useCallback(async (id) => {
		await fetchData(`utility-strike/${id}/`, (form) => {
			setUtilityStrikeForms(prevForms => ({
				...prevForms,
				[form.id]: {
					...form,
					formId: form.id,
					reporter_name: form.reporter_name,
					status: form.submission_time == null ? "Draft" : "Submitted"
				}
			}));
		});
	}, [fetchData]);

	return { utilityStrikeForms, setUtilityStrikeForms, getUtilityStrikeForms, getUtilityStrikeFormDetails, saveUtilityStrikeForm };
};
